import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import {
  BotFaq,
  BotFaqInput,
  Tutor,
  TutorService,
} from '../../../services/tutor.service';
import { AgentRefreshService } from '../../../agent/agent-refresh.service';

type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

interface DraftRow extends BotFaqInput {
  id?: string;
  /** Tags entered by the admin as a comma-separated string — converted
   *  to/from `tags: string[]` on save. Keeps the editor simple while
   *  the backend stays normalised. */
  tagsRaw?: string;
  status: SaveStatus;
  /** Per-row debounce timer for blur-based auto-save. Prevents spamming
   *  the backend when the admin is mid-typing. */
  saveTimer?: any;
}

/** Resizable column keys → their current pixel width. `active` and
 *  `actions` stay fixed; only the four text columns can be dragged. */
type ResizableCol = 'topic' | 'tags' | 'question' | 'answer';

@Component({
  selector: 'app-faq-list-form',
  templateUrl: './faq-list-form.component.html',
  styleUrl: './faq-list-form.component.scss',
})
export class FaqListFormComponent implements OnInit, OnDestroy {
  @Input() tutor?: Tutor;
  /** Mirrors the `bot_faq_enabled` toggle above the table. When false
   *  the whole editor is greyed out + non-interactive. */
  @Input() enabled = true;

  rows: DraftRow[] = [];
  loading = false;
  /** True while a CSV import is being parsed + uploaded. Disables the
   *  import/template buttons so a double-click can't fire two imports. */
  importing = false;

  // --- Search + pagination (mirrors the admin/learners table) --------
  searchText = '';
  currentPage = 0; // 0-based, matches app-pagination
  pageSize = 25;
  pageSizeOptions = [25, 50, 100, 250];

  // --- Resizable columns ---------------------------------------------
  /** Persisted per-column widths (px). Fixed columns live in the grid
   *  template directly. */
  colWidths: Record<ResizableCol, number> = {
    topic: 140,
    tags: 150,
    question: 340,
    answer: 380,
  };
  private static readonly MIN_COL_WIDTH = 80;
  private static readonly COL_WIDTHS_STORAGE_KEY = 'faq-col-widths';
  /** Active drag state, null when not resizing. */
  private resize?: {
    col: ResizableCol;
    startX: number;
    startWidth: number;
  };

  // --- Row action (3-dot) menu ---------------------------------------
  /** The row whose action menu is currently open, or null. */
  menuRow: DraftRow | null = null;

  /** ms to wait after the last keystroke before auto-saving. Long
   *  enough that typing fast doesn't fire dozens of writes; short
   *  enough that switching tabs / closing the page doesn't lose work. */
  private static readonly AUTOSAVE_DEBOUNCE_MS = 1200;

  private destroy$ = new Subject<void>();

  constructor(
    private tutorService: TutorService,
    private toastr: NbToastrService,
    private translate: TranslateService,
    private agentRefresh: AgentRefreshService,
  ) {}

  ngOnInit(): void {
    this.restoreColWidths();
    this.reload();
    // The in-house agent changed an FAQ: reload, unless the admin is mid-edit —
    // their pending auto-save would otherwise be thrown away.
    this.agentRefresh
      .on('faqs')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.rows.some((r) => r.status === 'dirty' || r.status === 'saving')) this.reload();
      });
  }

  ngOnDestroy(): void {
    // Flush any pending debounced saves so the user doesn't lose
    // mid-typed entries when navigating away.
    for (const row of this.rows) {
      if (row.saveTimer) {
        clearTimeout(row.saveTimer);
        if (row.status === 'dirty' && row.question && row.answer) {
          this.save(row);
        }
      }
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Pull the current FAQs from the backend and rebuild the editor
   *  rows. Called once on init and again after Save / Delete. */
  reload(): void {
    if (!this.tutor) return;
    this.loading = true;
    this.tutorService
      .listBotFaqs(this.tutor.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (faqs) => {
          this.rows = faqs.map((f) => this.toRow(f));
          this.loading = false;
          this.clampPage();
        },
        error: (err) => {
          this.loading = false;
          this.toastr.danger(
            this.translate.instant('pages.tutors.faq.loadError'),
            this.translate.instant('toasts.error'),
          );
          console.error('[faq-list-form] load failed', err);
        },
      });
  }

  // ===================================================================
  // Search + pagination
  // ===================================================================

  /** All rows matching the current search term (topic / tags / question
   *  / answer, case-insensitive). Local drafts always show so a
   *  freshly-added empty row can't be filtered out of sight. */
  get filteredRows(): DraftRow[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.rows;
    return this.rows.filter((r) => {
      if (!r.id) return true; // unsaved draft — keep visible
      return [r.topic, (r.tags || []).join(' '), r.question, r.answer]
        .filter((v): v is string => !!v)
        .some((v) => v.toLowerCase().includes(q));
    });
  }

  /** The current page slice of `filteredRows` — iterate this in the
   *  template. */
  get pagedRows(): DraftRow[] {
    const rows = this.filteredRows;
    const maxPage = Math.max(Math.ceil(rows.length / this.pageSize) - 1, 0);
    const page = Math.min(this.currentPage, maxPage);
    const start = page * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }

  resetPage(): void {
    this.currentPage = 0;
  }

  onPageChange(page: number): void {
    const totalPages = Math.ceil(this.filteredRows.length / this.pageSize);
    this.currentPage = Math.max(0, Math.min(page, Math.max(totalPages - 1, 0)));
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.resetPage();
  }

  private clampPage(): void {
    const totalPages = Math.ceil(this.filteredRows.length / this.pageSize);
    this.currentPage = Math.max(0, Math.min(this.currentPage, Math.max(totalPages - 1, 0)));
  }

  addRow(): void {
    this.rows.push({
      topic: '',
      tagsRaw: '',
      tags: [],
      question: '',
      answer: '',
      is_active: true,
      sort_order: this.rows.length,
      status: 'dirty',
    });
    // Make the new row visible: clear any active filter and jump to the
    // last page where it was appended.
    this.searchText = '';
    this.currentPage = Math.max(
      Math.ceil(this.rows.length / this.pageSize) - 1,
      0,
    );
  }

  /** Persist a single row. Existing rows go through PUT, new rows
   *  through POST. Index is resolved from the row identity so search /
   *  pagination never point us at the wrong entry. */
  save(row: DraftRow): void {
    if (!this.tutor) return;
    if (!row.question?.trim() || !row.answer?.trim()) {
      // Empty rows can't be saved — keep them as drafts so the user
      // can finish typing without losing what they have so far.
      return;
    }

    const index = this.rows.indexOf(row);
    if (index === -1) return;

    const payload: BotFaqInput = {
      topic: row.topic ?? '',
      tags: row.tags ?? [],
      question: row.question.trim(),
      answer: row.answer.trim(),
      is_active: row.is_active ?? true,
      sort_order: row.sort_order ?? index,
    };

    row.status = 'saving';
    const obs$ = row.id
      ? this.tutorService.updateBotFaq(this.tutor.id, row.id, payload)
      : this.tutorService.createBotFaq(this.tutor.id, payload);

    obs$.subscribe({
      next: (saved) => {
        const fresh = this.toRow(saved as BotFaq);
        // Preserve the user's pending edits if they kept typing while
        // the request was in flight — only swap server-confirmed
        // identifying fields, not the draft text.
        this.rows[index] = {
          ...fresh,
          status: 'saved',
        };
        // Reset the "saved" badge to idle after a couple seconds so
        // the row goes back to its neutral look.
        setTimeout(() => {
          if (this.rows[index]?.status === 'saved') {
            this.rows[index].status = 'idle';
          }
        }, 1800);
      },
      error: (err) => {
        row.status = 'error';
        const detail = err?.error?.detail || err?.message || '';
        this.toastr.danger(
          detail
            ? `${this.translate.instant('pages.tutors.faq.saveError')}: ${detail}`
            : this.translate.instant('pages.tutors.faq.saveError'),
          this.translate.instant('toasts.error'),
          { duration: 6000 },
        );
        console.error('[faq-list-form] save failed', err);
      },
    });
  }

  /** Per-row debounced auto-save. Triggered from `(ngModelChange)` so
   *  every keystroke resets the timer. Keeps the UX in line with the
   *  rest of the AI-Settings page (toggles + text fields auto-persist). */
  scheduleSave(row: DraftRow): void {
    row.status = 'dirty';
    if (row.saveTimer) clearTimeout(row.saveTimer);
    row.saveTimer = setTimeout(() => {
      row.saveTimer = undefined;
      this.save(row);
    }, FaqListFormComponent.AUTOSAVE_DEBOUNCE_MS);
  }

  remove(row: DraftRow): void {
    this.menuRow = null;
    const index = this.rows.indexOf(row);
    if (!this.tutor || !row.id) {
      // Local-only draft — nothing to delete on the server.
      if (index !== -1) this.rows.splice(index, 1);
      this.clampPage();
      return;
    }
    this.tutorService.deleteBotFaq(this.tutor.id, row.id).subscribe({
      next: () => {
        const i = this.rows.indexOf(row);
        if (i !== -1) this.rows.splice(i, 1);
        this.clampPage();
      },
      error: (err) => {
        this.toastr.danger(
          this.translate.instant('pages.tutors.faq.deleteError'),
          this.translate.instant('toasts.error'),
        );
        console.error('[faq-list-form] delete failed', err);
      },
    });
  }

  // ===================================================================
  // Tag chips
  // ===================================================================

  /** Add a tag chip. Splits on comma so pasting a list still works,
   *  dedupes, then auto-saves. */
  onTagAdd(row: DraftRow, value: string): void {
    const parts = (value || '')
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (!parts.length) return;
    const set = new Set(row.tags || []);
    for (const p of parts) set.add(p);
    row.tags = Array.from(set);
    this.scheduleSave(row);
  }

  onTagRemove(row: DraftRow, tag: string): void {
    row.tags = (row.tags || []).filter((t) => t !== tag);
    this.scheduleSave(row);
  }

  /** Soft pastel palette — deterministic per tag text so identical tags
   *  always share a colour, different tags spread across the palette. */
  private static readonly TAG_COLORS = [
    '#DBEAFE',
    '#DCFCE7',
    '#FEF3C7',
    '#EDE9FE',
    '#FCE7F3',
    '#CCFBF1',
    '#E0E7FF',
    '#FFE4E6',
    '#FEE2E2',
    '#D1FAE5',
  ];

  tagColor(tag: string): string {
    const s = (tag || '').toLowerCase();
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (h * 31 + s.charCodeAt(i)) >>> 0;
    }
    const palette = FaqListFormComponent.TAG_COLORS;
    return palette[h % palette.length];
  }

  // ===================================================================
  // Row action (3-dot) menu
  // ===================================================================

  toggleMenu(event: MouseEvent, row: DraftRow): void {
    event.stopPropagation();
    this.menuRow = this.menuRow === row ? null : row;
  }

  isMenuOpen(row: DraftRow): boolean {
    return this.menuRow === row;
  }

  /** Close the row menu on any click outside a trigger/menu. */
  @HostListener('document:click')
  closeMenu(): void {
    this.menuRow = null;
  }

  // ===================================================================
  // Resizable columns
  // ===================================================================

  /** Grid template shared by the head row and every data row so the
   *  columns stay aligned. Fixed active (72px) + actions (56px). */
  get gridTemplateColumns(): string {
    const w = this.colWidths;
    // Answer is the flexible column: `minmax(px, 1fr)` fills the tile's
    // remaining width by default, but stays draggable (its dragged
    // width becomes the min). Fixed active (72px) + actions (56px).
    return `${w.topic}px ${w.tags}px ${w.question}px minmax(${w.answer}px, 1fr) 72px 56px`;
  }

  startResize(event: MouseEvent, col: ResizableCol): void {
    event.preventDefault();
    event.stopPropagation();
    this.resize = {
      col,
      startX: event.clientX,
      startWidth: this.colWidths[col],
    };
  }

  @HostListener('document:mousemove', ['$event'])
  onResizeMove(event: MouseEvent): void {
    if (!this.resize) return;
    const delta = event.clientX - this.resize.startX;
    this.colWidths[this.resize.col] = Math.max(
      FaqListFormComponent.MIN_COL_WIDTH,
      Math.round(this.resize.startWidth + delta),
    );
  }

  @HostListener('document:mouseup')
  onResizeEnd(): void {
    if (!this.resize) return;
    this.resize = undefined;
    this.persistColWidths();
  }

  private restoreColWidths(): void {
    try {
      const raw = localStorage.getItem(
        FaqListFormComponent.COL_WIDTHS_STORAGE_KEY,
      );
      if (!raw) return;
      const parsed = JSON.parse(raw);
      for (const key of ['topic', 'tags', 'question', 'answer'] as ResizableCol[]) {
        if (typeof parsed?.[key] === 'number') {
          this.colWidths[key] = Math.max(
            FaqListFormComponent.MIN_COL_WIDTH,
            parsed[key],
          );
        }
      }
    } catch {
      // Corrupt/blocked storage — keep the defaults.
    }
  }

  private persistColWidths(): void {
    try {
      localStorage.setItem(
        FaqListFormComponent.COL_WIDTHS_STORAGE_KEY,
        JSON.stringify(this.colWidths),
      );
    } catch {
      // Storage unavailable (private mode / quota) — non-fatal.
    }
  }

  private toRow(f: BotFaq): DraftRow {
    return {
      id: f.id,
      topic: f.topic ?? '',
      tagsRaw: (f.tags || []).join(', '),
      tags: f.tags || [],
      question: f.question,
      answer: f.answer,
      is_active: f.is_active,
      sort_order: f.sort_order,
      status: 'idle',
    };
  }

  // ===================================================================
  // CSV template + import
  // ===================================================================

  /** Column headers of the downloadable template, in fixed order.
   *  Import maps by these names (DE + EN aliases) so a re-ordered or
   *  header-less file still parses; see `mapHeader`. */
  private static readonly TEMPLATE_HEADERS = [
    'Thema',
    'Tags',
    'Frage',
    'Antwort',
    'Aktiv',
  ];

  /** Build a small example CSV and trigger a browser download. Uses a
   *  UTF-8 BOM so Excel opens umlauts correctly, and `;` as the tag
   *  separator inside the Tags cell (comma is the field delimiter). */
  downloadTemplate(): void {
    const header = FaqListFormComponent.TEMPLATE_HEADERS.join(',');
    const example = [
      this.csvField('Preise'),
      this.csvField('pricing;kosten'),
      this.csvField('Was kostet das Produkt?'),
      this.csvField('Der Basistarif kostet 9,90 € pro Monat.'),
      this.csvField('ja'),
    ].join(',');
    const csv = '﻿' + header + '\r\n' + example + '\r\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'faq-vorlage.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Handle a chosen CSV file: read → parse → bulk-create FAQs. */
  onCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset immediately so selecting the same file again re-triggers change.
    input.value = '';
    if (!file || !this.tutor) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = (reader.result as string) ?? '';
      let entries: BotFaqInput[];
      try {
        entries = this.parseCsv(text);
      } catch (err) {
        console.error('[faq-list-form] csv parse failed', err);
        this.toastr.danger(
          this.translate.instant('pages.tutors.faq.importError'),
          this.translate.instant('toasts.error'),
        );
        return;
      }
      if (!entries.length) {
        this.toastr.warning(
          this.translate.instant('pages.tutors.faq.importEmpty'),
          this.translate.instant('toasts.error'),
        );
        return;
      }
      this.bulkCreate(entries);
    };
    reader.onerror = () => {
      this.toastr.danger(
        this.translate.instant('pages.tutors.faq.importError'),
        this.translate.instant('toasts.error'),
      );
    };
    reader.readAsText(file, 'utf-8');
  }

  /** POST every parsed entry, then reload the list once. */
  private bulkCreate(entries: BotFaqInput[]): void {
    if (!this.tutor) return;
    this.importing = true;
    const base = this.rows.length;
    const calls = entries.map((e, i) =>
      this.tutorService.createBotFaq(this.tutor!.id, {
        ...e,
        sort_order: base + i,
      }),
    );
    forkJoin(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.importing = false;
          this.toastr.success(
            this.translate.instant('pages.tutors.faq.importSuccess', {
              count: entries.length,
            }),
            this.translate.instant('toasts.success'),
          );
          this.reload();
        },
        error: (err) => {
          this.importing = false;
          const detail = err?.error?.detail || err?.message || '';
          this.toastr.danger(
            detail
              ? `${this.translate.instant('pages.tutors.faq.importError')}: ${detail}`
              : this.translate.instant('pages.tutors.faq.importError'),
            this.translate.instant('toasts.error'),
            { duration: 6000 },
          );
          console.error('[faq-list-form] bulk import failed', err);
          // Some rows may have been created before the failure — refresh
          // so the admin sees the actual server state, not a stale list.
          this.reload();
        },
      });
  }

  /** RFC-4180-ish CSV parser. Auto-detects the field delimiter
   *  (`,` `;` or tab) from the first line, honours double-quoted
   *  fields (embedded delimiters, newlines and `""` escapes) and maps
   *  columns by header name so column order doesn't matter. Rows
   *  missing question or answer are skipped. */
  private parseCsv(text: string): BotFaqInput[] {
    const stripped = text.replace(/^﻿/, '');
    const firstLine = stripped.split(/\r?\n/, 1)[0] ?? '';
    const delimiter = this.detectDelimiter(firstLine);
    const table = this.tokenizeCsv(stripped, delimiter);
    if (!table.length) return [];

    // Detect a header row: does the first row contain a recognisable
    // question/answer column name?
    const firstRow = table[0].map((c) => c.trim().toLowerCase());
    const hasHeader = firstRow.some(
      (c) => this.mapHeader(c) === 'question' || this.mapHeader(c) === 'answer',
    );

    let colMap: Record<string, number>;
    let dataRows: string[][];
    if (hasHeader) {
      colMap = {};
      firstRow.forEach((c, i) => {
        const key = this.mapHeader(c);
        if (key && !(key in colMap)) colMap[key] = i;
      });
      dataRows = table.slice(1);
    } else {
      // No header → assume fixed template order.
      colMap = { topic: 0, tags: 1, question: 2, answer: 3, active: 4 };
      dataRows = table;
    }

    const qIdx = colMap['question'];
    const aIdx = colMap['answer'];
    if (qIdx === undefined || aIdx === undefined) {
      // Can't locate the mandatory columns — treat as a bad file.
      throw new Error('missing question/answer columns');
    }

    const out: BotFaqInput[] = [];
    for (const row of dataRows) {
      // Skip fully blank lines.
      if (row.every((c) => !c || !c.trim())) continue;
      const question = (row[qIdx] ?? '').trim();
      const answer = (row[aIdx] ?? '').trim();
      if (!question || !answer) continue;
      out.push({
        topic: colMap['topic'] !== undefined ? (row[colMap['topic']] ?? '').trim() : '',
        tags:
          colMap['tags'] !== undefined
            ? this.splitTags(row[colMap['tags']], delimiter)
            : [],
        question,
        answer,
        is_active:
          colMap['active'] !== undefined
            ? this.parseBool(row[colMap['active']])
            : true,
      });
    }
    return out;
  }

  /** Pick the delimiter that occurs most in the header line. Defaults
   *  to comma when nothing stands out. */
  private detectDelimiter(line: string): string {
    const candidates = [',', ';', '\t'];
    let best = ',';
    let bestCount = -1;
    for (const d of candidates) {
      const count = line.split(d).length - 1;
      if (count > bestCount) {
        best = d;
        bestCount = count;
      }
    }
    return best;
  }

  /** Split raw text into a table of string cells, quote-aware. */
  private tokenizeCsv(text: string, delimiter: string): string[][] {
    const rows: string[][] = [];
    let field = '';
    let row: string[] = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          field += ch;
        }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === delimiter) {
        row.push(field);
        field = '';
      } else if (ch === '\n' || ch === '\r') {
        // Consume \r\n as a single break.
        if (ch === '\r' && text[i + 1] === '\n') i++;
        row.push(field);
        rows.push(row);
        field = '';
        row = [];
      } else {
        field += ch;
      }
    }
    // Flush the trailing field/row if the file didn't end with a newline.
    if (field.length > 0 || row.length > 0) {
      row.push(field);
      rows.push(row);
    }
    return rows;
  }

  /** Header cell → canonical column key (DE + EN aliases). */
  private mapHeader(cell: string): string | undefined {
    const c = cell.trim().toLowerCase();
    if (['thema', 'topic', 'theme'].includes(c)) return 'topic';
    if (['tags', 'schlagworte', 'schlagwörter'].includes(c)) return 'tags';
    if (['frage', 'question'].includes(c)) return 'question';
    if (['antwort', 'answer'].includes(c)) return 'answer';
    if (['aktiv', 'active', 'status'].includes(c)) return 'active';
    return undefined;
  }

  /** Tags cell → array. Splits on any of `;`, `|`, newline, plus comma
   *  when comma is NOT the CSV field delimiter. */
  private splitTags(raw: string | undefined, delimiter: string): string[] {
    if (!raw) return [];
    const seps = [';', '|', '\n'];
    if (delimiter !== ',') seps.push(',');
    const re = new RegExp('[' + seps.map((s) => (s === '\n' ? '\\n' : s)).join('') + ']');
    const parts = raw
      .split(re)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return Array.from(new Set(parts));
  }

  /** Forgiving boolean parse for the Aktiv column. Empty → true. */
  private parseBool(raw: string | undefined): boolean {
    if (raw === undefined) return true;
    const v = raw.trim().toLowerCase();
    if (v === '') return true;
    return ['ja', 'yes', 'true', '1', 'x', 'aktiv', 'active'].includes(v);
  }

  /** Quote a CSV field for the template if it contains a delimiter,
   *  quote or newline. */
  private csvField(value: string): string {
    if (/[",\n\r]/.test(value)) {
      return '"' + value.replace(/"/g, '""') + '"';
    }
    return value;
  }
}
